import React from 'react';
import { Link } from 'react-router-dom';
import cx from 'classnames';

import { Button, ButtonProps } from '@motorway/mw-highway-code';
import { THCComponentProps } from '@motorway/mw-highway-code/src/types';
import { filterDataProps } from '@motorway/mw-highway-code/src/utils/helpers';

import { Frame } from '../../../postSale/components/Frame/Frame';

import styles from './ActionCenter.scss';

export type ActionCenterVariant = 'light-blue' | 'light-purple' | 'light-yellow' | 'blue' | 'black' | 'yellow';
type CTASize = 'small' | 'medium' | 'large';

type UrlToProps = Partial<{
  hash: string;
  pathname: string;
  search: string;
}>

type UrlProps = Partial<{
  onClick: () => void;
  rel: string;
  state: Record<string, any>;
  target: string;
  to: UrlToProps | string;
}>

export type ActionCenterProps = THCComponentProps<{
  ctaIcon?: ButtonProps<'a'>['icon'];
  ctaLabel?: React.ReactNode;
  ctaSize?: CTASize;
  description?: React.ReactNode;
  descriptionElement?: keyof Pick<JSX.IntrinsicElements, 'div' | 'p' | 'span'>;
  heading?: React.ReactNode;
  hideImageOnMobile?: boolean;
  image?: React.ReactNode;
  nested?: boolean;
  url?: UrlProps;
  variant?: ActionCenterVariant;
}>;

export const ActionCenter = ({
  ctaIcon,
  ctaLabel,
  ctaSize = 'medium',
  description,
  descriptionElement: DE = 'p',
  heading,
  hideImageOnMobile = true,
  image,
  nested = false,
  url,
  variant = 'blue',
  ...rest
}: ActionCenterProps) => {
  const dataProps = filterDataProps(rest);
  const linkUrl = typeof url?.to === 'object' ? url.to.pathname : url?.to;
  const isRelativeUrl = linkUrl?.startsWith('/');
  const onClickOnly = !linkUrl && url?.onClick;

  const handleOnKeyDown = (e: React.KeyboardEvent) => {
    if (e.code === 'Enter') {
      url?.onClick?.();
    }
  };

  const content = (
    <Frame className={cx(styles.actionCenter, {
      [styles.nested]: nested,
      [styles.clickable]: Boolean(url?.to || url?.onClick),
    })}
    {...dataProps}
    data-variant={variant}
    >
      <div className={styles.content}>
        {heading && <h3 className={styles.heading}>{heading}</h3>}
        {description && <DE data-thc-style-override className={styles.description}>{description}</DE>}
        {ctaLabel && <Button data-thc-button icon={ctaIcon} label={ctaLabel} size={ctaSize} />}
      </div>
      {image && <div className={cx(styles.image, { [styles.hide]: hideImageOnMobile })}><img alt="none" src={image as string} /></div>}
    </Frame>
  );

  if (!linkUrl && !onClickOnly) {
    return content;
  }

  const anchorProps = {
    href: linkUrl,
    onClick: url?.onClick,
    ...(onClickOnly && { onKeyDown: handleOnKeyDown, tabIndex: 0 }),
    rel: 'noopener noreferrer',
    target: url?.target,
  };

  const linkContent = (linkUrl && isRelativeUrl
    ? <Link {...url} className={styles.actionCenterLink} to={url?.to!}>{content}</Link>
    : <a {...anchorProps} className={styles.actionCenterLink}>{content}</a>
  );

  return linkContent;
};
